import React, { useState, useRef, useEffect } from 'react';
import { auth, db } from '../../firebase';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { doc, updateDoc } from 'firebase/firestore';
import { sendResetPasswordEmailApi } from '../../services/firebaseApiService';
import OAuthButtons from './OAuthButtons';
import '../../styles/LoginPopup.css';

const LoginPopup = ({
  onClose,
  initialEmail = '',
  hintMessage = '',
  foxChatResetEmailAttachHooksRef = null,
}) => {
  const [email, setEmail] = useState(initialEmail);
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitDots, setSubmitDots] = useState(1);
  const [error, setError] = useState('');
  const [resetSent, setResetSent] = useState(false);
  const [resetError, setResetError] = useState('');
  const [resetCooldown, setResetCooldown] = useState(0);
  const [resetInFlight, setResetInFlight] = useState(false);
  const popupRef = useRef(null);

  useEffect(() => {
    setEmail(initialEmail);
  }, [initialEmail]);

  useEffect(() => {
    setResetSent(false);
    setResetError('');
  }, [error]);

  useEffect(() => {
    if (resetCooldown <= 0) return;
    const timer = setTimeout(() => setResetCooldown(resetCooldown - 1), 1000);
    return () => clearTimeout(timer);
  }, [resetCooldown]);

  /* Cycle "Signing in." / ".." / "..." while the request is in flight. */
  useEffect(() => {
    if (!isSubmitting) {
      setSubmitDots(1);
      return undefined;
    }
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setSubmitDots(3);
      return undefined;
    }
    const id = window.setInterval(() => {
      setSubmitDots((n) => (n % 3) + 1);
    }, 400);
    return () => window.clearInterval(id);
  }, [isSubmitting]);

  // Close when clicking outside the popup
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (resetInFlight) return;
      if (popupRef.current && !popupRef.current.contains(event.target)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose, resetInFlight]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address (e.g., name@example.com)');
      setIsSubmitting(false);
      return;
    }

    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      // Link anonymous inline survey response to this user (if they completed survey this session)
      const lastSurveyResponseId = sessionStorage.getItem('lastSurveyResponseId');
      if (lastSurveyResponseId) {
        try {
          await updateDoc(doc(db, 'survey_responses', lastSurveyResponseId), { userId: user.uid });
          sessionStorage.removeItem('lastSurveyResponseId');
          console.log('Linked survey response to user');
        } catch (linkError) {
          console.error('Error linking survey response to user:', linkError);
        }
      }

      onClose();
    } catch (error) {
      console.error('Error signing in:', error);
      // Log the specific error code to help with debugging
      console.log('Firebase error code:', error.code);
      
      // More specific error handling
      if (error.code === 'auth/invalid-email') {
        setError('Please enter a valid email address (e.g., name@example.com)');
      } else if (error.code === 'auth/wrong-password') {
        setError('Incorrect password. Please try again.');
      } else if (error.code === 'auth/user-not-found') {
        setError('No account found with this email. Please sign up first.');
      } else if (error.code === 'auth/invalid-credential') {
        setError('Invalid email or password. Please check your credentials and try again.');
      } else if (error.code === 'auth/too-many-requests') {
        setError('Too many failed login attempts. Please try again later.');
      } else {
        // For any other errors, just show a generic message without the technical details
        setError('There was a problem signing in. Please try again.');
        // But still log the full error for debugging
        console.log('Unhandled auth error:', error);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (resetCooldown > 0) return;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setResetError('Please enter a valid email first.');
      return;
    }
    setResetError('');
    setResetSent(false);
    setIsSubmitting(true);
    setResetInFlight(true);
    try {
      try {
        window.localStorage.setItem('emailForSignIn', email.trim().toLowerCase());
      } catch (_) {}
      const hooks = foxChatResetEmailAttachHooksRef?.current;
      let foxChatPoster = null;
      if (hooks?.buildPosterForResetEmail) {
        try {
          foxChatPoster = await hooks.buildPosterForResetEmail();
        } catch (buildErr) {
          console.warn('FoxChat poster build for reset email failed:', buildErr);
        }
      }
      const resetOptions =
        foxChatPoster && typeof foxChatPoster === 'object' ? { foxChatPoster } : {};
      const data = await sendResetPasswordEmailApi(email.trim(), resetOptions);
      setResetSent(true);
      setResetCooldown(60);
      if (data.attachedPosterId && hooks?.onPosterAttachedViaResetEmail) {
        try {
          hooks.onPosterAttachedViaResetEmail({ posterId: data.attachedPosterId });
        } catch (cbErr) {
          console.warn('onPosterAttachedViaResetEmail failed:', cbErr);
        }
      }
    } catch (err) {
      const rateLimited =
        err.status === 429 ||
        err.code === 'rate_limited' ||
        err.message === 'Please wait before requesting another email';
      if (rateLimited) {
        const seconds = Number(err.retryAfterSeconds);
        setResetError('');
        setResetSent(true);
        setResetCooldown(Number.isFinite(seconds) && seconds > 0 ? Math.ceil(seconds) : 60);
      } else {
        setResetError(err.message || 'Something went wrong. Try again.');
        setResetCooldown(0);
      }
    } finally {
      setIsSubmitting(false);
      setResetInFlight(false);
    }
  };

  return (
    <div className="login-popup-overlay auth-popup signin-popup-overlay">
      <div
        className={`login-popup signin-popup${hintMessage ? ' signin-popup--with-hint' : ''}`}
        ref={popupRef}
      >
          <button
            className="close-button"
            onClick={onClose}
            disabled={resetInFlight}
            aria-label="Close"
          >
            ×
          </button>
          
          <div className="login-popup-header">
            <h2>Sign In</h2>
          </div>
          
          {hintMessage && (
            <div className="error-message auth-hint">{hintMessage}</div>
          )}

          <OAuthButtons onSuccess={onClose} />

          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                required
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="password">Password</label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                required
              />
            </div>

            {error && (
              <div className="error-message" style={{ color: '#ff4444', textAlign: 'center' }}>
                {error}
              </div>
            )}
            
            <button
              type="submit"
              className="login-button"
              disabled={isSubmitting || !email || !password}
              aria-busy={isSubmitting}
              aria-label={isSubmitting ? 'Signing in' : undefined}
            >
              {isSubmitting ? (
                <>
                  Signing in
                  <span className="signup-submitting-dots" aria-hidden="true">
                    {'.'.repeat(submitDots)}
                  </span>
                </>
              ) : (
                'Sign In'
              )}
            </button>

            <div className="form-reset-wrap">
              <a
                href="#"
                className={`reset-password-link${resetCooldown > 0 || isSubmitting ? ' disabled' : ''}`}
                onClick={handleResetPassword}
                style={resetCooldown > 0 || isSubmitting ? { pointerEvents: 'none', opacity: 0.5 } : {}}
              >
                {resetCooldown > 0 ? `Resend in ${resetCooldown}s` : 'Forgot your password?'}
              </a>
              {resetSent && (
                <span className="reset-sent">
                  Check your email and spam folder for the link to set or reset your password.
                </span>
              )}
              {resetError && <span className="reset-error">{resetError}</span>}
            </div>
          </form>
        </div>
    </div>
  );
};

export default LoginPopup; 